// apps/client/src/ui/chat/ChatLog.tsx (M0-T19) — the recent town chat, newest at the bottom; names in their friend colour.
// M2-T08: blocked players' lines are hidden, and another resident's line has a small Report action.
import { friendColors } from '@whiskerton/content';
import { useEffect, useMemo, useRef } from 'react';
import { safetyBlockedNow, visibleChatLines } from '../../state/slices/safety';
import { useGame } from '../../state/store';
import { useT } from '../i18n';

/** A small flag glyph after another resident's line: tap to report it (the label carries the words). */
const REPORT_STYLE = { marginLeft: 6, verticalAlign: 'middle', width: 30, height: 30, display: 'inline-grid', placeItems: 'center', padding: 0, border: 0,
  borderRadius: '50%', background: 'transparent', color: 'var(--wood-600)', cursor: 'pointer' } as const;
const FLAG = (
  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
    <path d="M3 1.5v13M3 2.5h8.5l-2 3 2 3H3" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function ChatLog() {
  const t = useT();
  const all = useGame((s) => s.chatLines);
  const residents = useGame((s) => s.residents);
  const blocked = useGame(safetyBlockedNow);
  const you = useGame((s) => s.you?.resident ?? null);
  const townId = useGame((s) => s.townId);
  const lines = useMemo(() => visibleChatLines(all, residents, blocked), [all, residents, blocked]);
  const box = useRef<HTMLOListElement>(null);
  useEffect(() => { const el = box.current; if (el) el.scrollTop = el.scrollHeight; }, [lines]);
  if (!lines.length) return <p className="chat-empty">{t('chat.empty')}</p>;
  return (
    <ol ref={box} className="chat-log" aria-label={t('chat.log')} aria-live="polite">
      {lines.map((l) => {
        const color = residents[l.r]?.color;
        return (
          <li key={l.id} data-chat-id={l.id}>
            <span className="chat-name" style={color ? { color: friendColors[color].inkOnWhite } : undefined}>{l.name}</span> {l.text}
            {townId && l.r !== you && (
              <button type="button" className="chat-report" aria-label={t('safety.reportChat')} title={t('safety.reportChat')} style={REPORT_STYLE}
                onClick={() => useGame.getState().openScreen('report', { townId, kind: 'chat', refId: l.id, preview: l.text })}>{FLAG}</button>
            )}
          </li>
        );
      })}
    </ol>
  );
}
